import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useInApp from '../../shared/hooks/useInApp'
import useEntitlement from '../../shared/hooks/useEntitlement'
import Footer from '../../shared/components/UIElements/Footer'
import { getOrCreateIapUserId } from '../../shared/components/Navigation/iapUserId'
import './PlayerHistorySearch.css'

const PlayerHistorySearch = () => {
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [players, setPlayers] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const { hasIap } = useInApp()
	const userId = getOrCreateIapUserId()
	const entitlement = useEntitlement(hasIap, userId)

	useEffect(() => {
		const name = search.trim()

		if (name.length < 2) {
			setPlayers([])
			setLoading(false)
			setError('')
			return
		}

		const controller = new AbortController()

		const timer = setTimeout(async () => {
			setLoading(true)
			setError('')

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player-history/search?name=${encodeURIComponent(name)}`,
					{
						signal: controller.signal,
						headers: {
							'x-user-id': userId,
						},
					},
				)

				if (!response.ok) {
					throw new Error('Unable to search players.')
				}

				const data = await response.json()
				setPlayers(data.players || [])
			} catch (err) {
				if (err.name !== 'AbortError') {
					setError('Something went wrong searching for players.')
					setPlayers([])
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false)
				}
			}
		}, 400)

		return () => {
			clearTimeout(timer)
			controller.abort()
		}
	}, [search, userId])

	/* if (!hasIap || entitlement !== 'active') {
		return (
			<div className='phs-access-denied'>
				<h2>Player History</h2>
				<p>
					This feature is available exclusively to active Shirey Tournament
					Tracker app subscribers.
				</p>
				<button onClick={() => navigate('/')}>Return Home</button>
			</div>
		)
	} */

	return (
		<div className='phs-page'>
			<div className='phs-header'>
				<button
					type='button'
					className='phs-back'
					onClick={() => navigate('/')}
				>
					‹
				</button>

				<h1>PLAYER HISTORY</h1>
			</div>

			<div className='phs-content'>
				<h2>Find a Player</h2>

				<p className='phs-description'>
					Search for a player to view their career statistics across all
					participating leagues and tournaments.
				</p>

				<div className='phs-search-box'>
					<input
						type='search'
						placeholder='Enter player name...'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						autoComplete='off'
					/>

					{search.trim().length >= 2 && (
						<div className='phs-dropdown'>
							{loading ? (
								<div className='phs-dropdown-message'>Searching players...</div>
							) : error ? (
								<div className='phs-dropdown-message'>{error}</div>
							) : players.length === 0 ? (
								<div className='phs-dropdown-message'>No players found.</div>
							) : (
								players.map((player) => (
									<div
										className='phs-dropdown-item'
										key={`${player.firstName}-${player.lastName}`}
										role='button'
										tabIndex={0}
										onClick={() => {
											navigate(
												`/master-player-history?firstName=${encodeURIComponent(player.firstName)}&lastName=${encodeURIComponent(player.lastName)}`,
											)
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault()
												navigate(
													`/master-player-history?firstName=${encodeURIComponent(player.firstName)}&lastName=${encodeURIComponent(player.lastName)}`,
												)
											}
										}}
									>
										<div className='phs-dropdown-name'>
											{player.firstName} {player.lastName}
										</div>
										<div className='phs-dropdown-orgs'>
											{[
												...new Set(
													player.sources.map((source) => {
														const names = {
															clubs: 'Clubs',
															coolHockey: 'Cool Hockey Events',
															futureStars: 'Future Stars',
															nsbhl: 'NSBHL',
															teamPitt: 'Team Pitt',
															gtha: 'GTHA',
															murrysville: 'Murrysville',
														}

														return (
															names[source.organization] || source.organization
														)
													}),
												),
											].join(' • ')}
										</div>
									</div>
								))
							)}
						</div>
					)}
				</div>

				<div className='phs-info'>
					<h3>One Player. Every Organization.</h3>
					<p>
						Explore player history from Team Pitt, Murrysville, NSBHL, Future
						Stars, Cool Hockey Events, and more.
					</p>
				</div>
			</div>
			<div className='phs-footer'>
				<Footer />
			</div>
		</div>
	)
}

export default PlayerHistorySearch
